window.utilities = window.utilities || {};
window.shopifyVariables = window.shopifyVariables || {};

window.shopifyVariables = {
  MOBILE_BREAKPOINT: 963,
};

/**
 * Checks if a given breakpoint is considered mobile.
 * @param {number|string} breakpoint - Breakpoint value (px). Can be number or string like '768px'.
 * @param {number} [mobileMax=768] - Max mobile width threshold in pixels.
 * @returns {boolean} - Returns true if mobile, false otherwise.
 */
window.utilities.isMobileBreakpoint = (mobileMax = 964) => {
  const currentWidth = window.innerWidth;
  return currentWidth < mobileMax;
};
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  ).filter(window.utilities.isElementVisible);
}

/**
 * Utility function to check if an element is visible in the viewport.
 *
 * @param {HTMLElement} element - The element to check visibility for.
 * @returns {boolean} - Returns true if the element is visible, otherwise false.
 * Checks for element's size, visibility, display property, and if it's disabled.
 */
window.utilities.isElementVisible = (element) => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const styles = window.getComputedStyle(element);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    styles.visibility !== 'hidden' &&
    styles.display !== 'none' &&
    !element.disabled
  );
};

/**
 * Utility function to get all focusable elements within a container.
 * Focusable elements include links, buttons, inputs, etc., that are visible.
 *
 * @param {HTMLElement} container - The container element to query for focusable elements.
 * @returns {HTMLElement[]} - Returns an array of focusable elements within the container.
 */
window.utilities.getFocusableElements = (container) => {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  ).filter(window.utilities.isElementVisible);
};

/**
 * Utility function to create a delay or pause for a specified amount of time.
 *
 * @param {number} ms - The amount of time in milliseconds to delay.
 * @returns {Promise} - Returns a promise that resolves after the specified delay.
 */
window.utilities.delay = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Utility function to wait for all images within a container to load.
 *
 * @param {HTMLElement} container - The container element that holds the images.
 * @returns {Promise} - Returns a promise that resolves when all images in the container are loaded.
 */
window.utilities.waitForImagesToLoad = async (container) => {
  const images = container.querySelectorAll('img');
  return Promise.all(
    [...images].map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = img.onerror = resolve;
      });
    }),
  );
};

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', focusTrapEvents.focusin);
  document.removeEventListener('focusout', focusTrapEvents.focusout);
  document.removeEventListener('keydown', focusTrapEvents.keydown);

  if (elementToFocus) elementToFocus.focus();
}

window.utilities.removeTrapFocus = removeTrapFocus;

function moveFocus(elements, current, direction) {
  const index = elements.indexOf(current);
  if (index !== -1) {
    const nextIndex = (index + direction + elements.length) % elements.length;
    elements[nextIndex].focus();
  }
}

const focusTrapEvents = {};

window.utilities.trapFocus = (container, elementToFocus) => {
  if (!container) return;

  const elements = window.utilities.getFocusableElements(container);

  if (elements.length === 0) return;

  const first = elements[0];
  const last = elements[elements.length - 1];
  let focusElement = elementToFocus;

  if (!focusElement) {
    const formInput = container.querySelector(
      'form input[type="text"], form input[type="search"], form input[type="email"], form input[type="url"]',
    );
    focusElement = formInput || first;
  }

  removeTrapFocus();

  // Ensure focus remains in modal
  focusTrapEvents.focusin = (event) => {
    if (!container.contains(event.target)) {
      event.preventDefault();
      first.focus();
    }
    document.addEventListener('keydown', focusTrapEvents.keydown);
  };

  focusTrapEvents.focusout = (event) => {
    if (!container.contains(event.relatedTarget)) {
      event.preventDefault();
      first.focus();
    }
    document.removeEventListener('keydown', focusTrapEvents.keydown);
  };

  focusTrapEvents.keydown = (event) => {
    const { key, shiftKey, target } = event;

    if (key === 'Tab') {
      // Tab forward from last element
      if (target === last && !shiftKey) {
        event.preventDefault();
        first.focus();
      }
      // Tab backward from first element
      if ((target === container || target === first) && shiftKey) {
        event.preventDefault();
        last.focus();
      }
    }

    if (['ArrowUp'].includes(key)) {
      event.preventDefault();
      moveFocus(elements, target, -1);
    }

    if (['ArrowDown'].includes(key)) {
      event.preventDefault();
      moveFocus(elements, target, 1);
    }
  };

  document.addEventListener('focusout', focusTrapEvents.focusout);
  document.addEventListener('focusin', focusTrapEvents.focusin);

  focusElement.focus();

  if (
    focusElement?.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(focusElement.type) &&
    focusElement.value
  ) {
    focusElement.setSelectionRange(0, focusElement.value.length);
  }
};

/**
 * Parses an HTML string, wraps it in a div, and returns the div element.
 *
 * @param {string} html - The HTML string to parse.
 * @returns {HTMLDivElement} - A div element containing the parsed HTML.
 */
window.utilities.parseHTMLString = (html) => {
  const doc = new DOMParser().parseFromString(html.trim(), 'text/html');
  const wrapperDiv = document.createElement('div');

  // Move all child nodes from body into the wrapper div
  Array.from(doc.body.childNodes).forEach((node) => {
    wrapperDiv.appendChild(node);
  });

  return wrapperDiv;
};

/**
 * Gracefully replaces innerHTML of a container with a fade-out/fade-in effect.
 *
 * @param {HTMLElement} container - The element to update.
 * @param {string} newHTML - The new innerHTML content.
 * @param {number} duration - Transition duration in ms (default: 300).
 * @param {Object} [options] - Optional hooks.
 * @param {Function} [options.onBeforeSwap] - Called before content is removed.
 * @param {Function} [options.onAfterSwap] - Called after new content is loaded.
 * @returns {Promise<void>} Resolves when complete.
 */
window.utilities.fadeSwap = async (container, newHTML, duration = 300, options = {}) => {
  if (!container) return;

  if (typeof options.onBeforeSwap === 'function') {
    await options.onBeforeSwap(container);
  }

  container.classList.add('is-fading');

  await window.utilities.delay(duration);

  container.innerHTML = newHTML;

  await window.utilities.waitForImagesToLoad(container);

  container.classList.remove('is-fading');

  if (typeof options.onAfterSwap === 'function') {
    await options.onAfterSwap(container);
  }
};

/**
 * Replaces attributes of one element with another (if they exist).
 * This function will copy all attributes, including special ones like `src` for <img> or `href` for <a>.
 *
 * @param {HTMLElement} targetElement - The element to be updated.
 * @param {HTMLElement} sourceElement - The element whose attributes will be copied to targetElement.
 */
window.utilities.replaceElementAttributes = (targetElement, sourceElement) => {
  if (!targetElement || !sourceElement) return;

  const sourceAttributes = sourceElement?.attributes;

  for (let i = 0; i < sourceAttributes.length; i++) {
    const attribute = sourceAttributes[i];

    if (attribute.name === 'src' || attribute.name === 'srcset' || attribute.name === 'href') {
      targetElement.setAttribute(attribute.name, sourceElement.getAttribute(attribute.name));
    } else {
      targetElement.setAttribute(attribute.name, attribute.value);
    }
  }

  if (sourceElement instanceof HTMLImageElement && targetElement instanceof HTMLImageElement) {
    const attributesToCopy = ['title', 'alt', 'src', 'classList', 'sizes', 'srcset'];

    attributesToCopy.forEach((attr) => {
      if (sourceElement.hasAttribute(attr)) {
        targetElement[attr] = sourceElement[attr];
      }
    });
  }
};

/**
 * Utility function to split an array into smaller chunks of a specified size.
 *
 * @param {Array} array - The array to be chunked.
 * @param {number} size - The size of each chunk.
 * @returns {Array} - A new array containing chunks of the original array.
 * Each chunk is an array of the specified size, except possibly the last one.
 */
window.utilities.arrayChunk = (array, size) => {
  const chunkedArr = [];
  for (let i = 0; i < array.length; i += size) {
    chunkedArr.push(array.slice(i, i + size));
  }
  return chunkedArr;
};

/**
 * Utility function to check if the device is mobile using a media query.
 *
 * @returns {boolean} true if the device is mobile, false otherwise.
 */
window.utilities.isMobile = () => {
  return window.matchMedia(`(max-width: ${window.shopifyVariables.MOBILE_BREAKPOINT}px)`).matches;
};

/**
 * Utility function to generate the Shopify product gid if it's not in gid format.
 *
 * @param {string|number} id - The product ID, either as a number or gid.
 * @returns {string} - Returns the gid for the product in the format gid://shopify/Product/{id}.
 */
window.utilities.generateGid = (id) => {
  const raw = String(id ?? '').trim();
  if (!raw) return '';

  const gidPattern = /^gid:\/\/shopify\/Product\/\d+$/;
  if (gidPattern.test(raw)) {
    return raw;
  }

  const gidWithHash = raw.match(/^gid:\/\/shopify\/Product\/(\d+)(?::[a-f0-9]{20,})?$/i);
  if (gidWithHash) {
    return `gid://shopify/Product/${gidWithHash[1]}`;
  }

  const numericWithHash = raw.match(/^(\d+)(?::[a-f0-9]{20,})?$/);
  if (numericWithHash) {
    return `gid://shopify/Product/${numericWithHash[1]}`;
  }

  if (!Number.isNaN(+raw)) {
    return `gid://shopify/Product/${raw}`;
  }

  return '';
};

/**
 * Utility function to extract the numeric ID from a Shopify GID.
 *
 * @param {string} gid - The GID string in the format gid://shopify/Product/{id}.
 * @returns {string|null} - The numeric ID as a string, or null if format is invalid.
 */
window.utilities.extractIdFromGid = (gid) => {
  const normalized = window.utilities.generateGid(gid) || String(gid ?? '');
  const match = normalized.match(/gid:\/\/shopify\/\w+\/(\d+)/);
  return match ? match[1] : null;
};

class ToggleableIconButton extends HTMLElement {
  connectedCallback() {
    if (this._listenerAdded) return;
    this._listenerAdded = true;

    this.button = this.querySelector('.js-toggle');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      this.button.classList.toggle('is-active');
    });
  }
}

customElements.define('toggleable-icon-button', ToggleableIconButton);

class SectionAnchor extends HTMLElement {
  constructor() {
    super();

    this.url = this.getAttribute('url') || window.location.href;
    this.textCopied = this.getAttribute('data-anchor-text-copied') || 'Copied!';
    this.textDefault = this.getAttribute('data-anchor-text-default') || 'Copy link to section';

    this.boundResetState = this.resetState.bind(this);
  }

  connectedCallback() {
    this.viewModel = this.getViewModel();

    if (!this.viewModel.$trigger) {
      console.warn('SectionAnchor: No trigger element found inside the component.');
      return;
    }

    this.bindEvents();
  }

  /**
   * Defines all the elements inside the component
   * @returns {Object} Object containing references to key elements
   */
  getViewModel() {
    return {
      $trigger: this.querySelector('.toggleable-icon-button'),
      $tooltip: this.querySelector('tooltip-component'),
      $tooltipInfo: this.querySelector('.shared-tooltip__info'),
      $liveRegion: this.querySelector('#section-anchor-link__live-region'),
    };
  }

  bindEvents() {
    const { $trigger } = this.viewModel;

    $trigger.addEventListener('click', (event) => this.copyToClipboard(event));
    $trigger.addEventListener('keypress', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        this.copyToClipboard(event);
      }
    });
  }

  copyToClipboard(event) {
    event.preventDefault();

    navigator.clipboard
      .writeText(window.location.origin + this.url)
      .then(() => {
        const { $liveRegion, $tooltipInfo } = this.viewModel;

        if ($liveRegion) {
          $liveRegion.textContent = this.textCopied;
        }

        if ($tooltipInfo) {
          $tooltipInfo.textContent = this.textCopied;
        }

        this.scheduleResetState();
        this.addBlurAndScrollListeners();
      })
      .catch((err) => {
        console.error('Copy failed', err);
      });
  }

  scheduleResetState() {
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(this.boundResetState, 3000);
  }

  addBlurAndScrollListeners() {
    this.viewModel.$trigger.addEventListener('mouseleave', this.boundResetState, { once: true });
    this.viewModel.$trigger.addEventListener('blur', this.boundResetState, { once: true });
  }

  resetState() {
    clearTimeout(this.resetTimer);

    const { $liveRegion, $tooltipInfo, $tooltip } = this.viewModel;

    if ($liveRegion) {
      $liveRegion.textContent = '';
    }

    if ($tooltipInfo) {
      $tooltipInfo.textContent = this.textDefault;
    }

    if ($tooltip && typeof $tooltip.closeTooltip === 'function') {
      $tooltip.closeTooltip();
    }
  }
}

customElements.define('section-anchor', SectionAnchor);

/**
 * Attempts to parse JSON and returns the fallback if there's an error.
 * @param {string} json
 * @param {Object} fallback
 * @returns
 */
window.utilities.safeParseJSON = (json, fallback = null) => {
  if (!json) {
    return fallback;
  }

  try {
    return JSON.parse(json);
  } catch (ex) {
    return fallback;
  }
};

/**
 * Generates srcset and sizes attributes for a Shopify CDN image.
 *
 * @param {string} fullUrl - The full Shopify CDN image URL (may include width and version).
 * @param {number[]} widths - An array of desired widths for the srcset.
 * @param {string} [defaultWidth='500'] - Default width to use for `src` attribute.
 * @param {string} [sizes] - Optional sizes attribute string.
 * @returns {{
 *   src: string,
 *   srcset: string,
 *   sizes?: string
 * }}
 */
window.utilities.generateSrcSet = (fullUrl, widths, defaultWidth = '500', sizes) => {
  const url = new URL(fullUrl, window.location.origin);

  // Clean width param but preserve others like "v"
  url.searchParams.delete('width');

  const baseUrl = url.origin + url.pathname;
  const preservedParams = url.searchParams.toString();
  const baseQuery = preservedParams ? `?${preservedParams}` : '';
  const connector = preservedParams ? '&' : '?';

  const srcset = widths.map((w) => `${baseUrl}${baseQuery}${connector}width=${w} ${w}w`).join(',');

  const src = `${baseUrl}${baseQuery}${connector}width=${defaultWidth}`;

  const result = { src, srcset };
  if (sizes) result.sizes = sizes;
  return result;
};

window.utilities.PRODUCT_CARD_IMAGE_WIDTHS = [165, 360, 533, 720, 940, 1066, 1200, 1500, 1600];
window.utilities.PRODUCT_CARD_IMAGE_DEFAULT_WIDTH = '940';
window.utilities.PRODUCT_CARD_IMAGE_MAX_WIDTH = 1600;

/**
 * Generates srcset/sizes for product tile images with a enforced max width (2x for ~800px slots).
 *
 * @param {string} fullUrl
 * @param {string} [sizes]
 * @returns {{ src: string, srcset: string, sizes?: string }}
 */
window.utilities.generateProductCardSrcSet = (fullUrl, sizes) => {
  const maxWidth = window.utilities.PRODUCT_CARD_IMAGE_MAX_WIDTH;
  const widths = window.utilities.PRODUCT_CARD_IMAGE_WIDTHS.filter((w) => w <= maxWidth);
  const resolvedSizes = sizes ?? window.utilities.getProductImageSizes?.() ?? '';

  return window.utilities.generateSrcSet(
    fullUrl,
    widths,
    window.utilities.PRODUCT_CARD_IMAGE_DEFAULT_WIDTH,
    resolvedSizes,
  );
};

const PRODUCT_IMAGE_SIZES = {
  MOBILE_BREAKPOINT: 575,
  DESKTOP_COLUMNS: 3,
  MOBILE_COLUMNS: 2,
};

let cachedProductImageSizes = null;

/**
 * Builds a responsive `sizes` attribute for product tile images based on grid CSS variables.
 *
 * @returns {string}
 */
window.utilities.getProductImageSizes = () => {
  if (cachedProductImageSizes) return cachedProductImageSizes;

  const style = getComputedStyle(document.documentElement);
  const desktopMargin = style.getPropertyValue('--grids-standard-margin').trim();
  const desktopGutter = style.getPropertyValue('--grids-standard-gutter').trim();
  const mobileMargin = style.getPropertyValue('--layout-standard-mobile-margin').trim();
  const mobileGutter = style.getPropertyValue('--layout-standard-mobile-gutter').trim();
  const { MOBILE_BREAKPOINT, DESKTOP_COLUMNS, MOBILE_COLUMNS } = PRODUCT_IMAGE_SIZES;
  const desktopGutters = DESKTOP_COLUMNS - 1;
  const mobileGutters = MOBILE_COLUMNS - 1;

  cachedProductImageSizes = [
    `(max-width: ${MOBILE_BREAKPOINT}px) calc((100vw - 2 * ${mobileMargin} - ${mobileGutters} * ${mobileGutter}) / ${MOBILE_COLUMNS})`,
    `calc((100vw - 2 * ${desktopMargin} - ${desktopGutters} * ${desktopGutter}) / ${DESKTOP_COLUMNS})`,
  ].join(', ');

  return cachedProductImageSizes;
};

window.utilities.NavigationKeyHandler = {
  setup({ closeModal }) {
    if (window.__navKeyHandlerInitialized) return;
    window.__navKeyHandlerInitialized = true;

    const navDOM = document.querySelectorAll('#desktop-main-menu, #mobile-drawer-trigger, #mobile-nav-slideout');

    navDOM.forEach((dom) => {
      dom.addEventListener('keydown', (e) => {
        window.utilities.NavigationKeyHandler.handleKeydown(e, { closeModal });
      });
    });
  },

  findLevel(ele) {
    let rtn_level = 'menu';
    if (ele.closest('.submenu') && ele.getAttribute('role') !== 'menuitem') {
      rtn_level = 'submenu';
    }
    return rtn_level;
  },

  handleActionKey(ele, level, has_submenu) {
    ele.click();
    if (has_submenu) {
      if (level === 'menu') {
        const target_id = 'submenu-' + ele.dataset.id;
        document.getElementById(target_id).querySelector('toggleable-icon-button .navigation-drawer__button').focus();
      } else {
        const target_id = ele.getAttribute('aria-controls');
        document.getElementById(target_id).querySelector('.navigation-drawer__button').focus();
      }
    }
  },

  handleVertArrow(ele, level, has_submenu, direction) {
    const isMobile = window.utilities.isMobile();
    if (level === 'menu') {
      if (has_submenu) {
        if (isMobile) ele.click();
        const target_id = 'submenu-' + ele.dataset.id;
        const target_children = document.getElementById(target_id).querySelectorAll('.navigation-drawer__button');
        if (target_children !== undefined)
          if (direction === 'up') {
            target_children[target_children.length - 1].focus();
          } else {
            target_children[0].focus();
          }
      } else {
        const target_children = ele.closest('[role="menubar"]').querySelectorAll('[role="menuitem"]');
        const curr_index = Array.from(target_children).indexOf(ele);

        if (target_children !== undefined) {
          let target_index;
          if (direction === 'up') {
            target_index = curr_index + 1;
            if (target_index >= target_children.length) {
              target_index = 0;
            }
          } else {
            target_index = curr_index - 1;
            if (target_index < 0) {
              target_index = target_children.length - 1;
            }
          }
          target_children[target_index]?.focus();
        }
      }
    } else {
      const target_level = ele.dataset.level;
      const temp_queryString = `.navigation-drawer__button[data-level="${target_level}"]`;

      const target_children = ele.closest('.submenu, .list-menu').querySelectorAll(temp_queryString);
      const curr_index = Array?.from(target_children)?.indexOf(ele);
      let target_index = curr_index;

      if (target_children !== undefined) {
        if (direction === 'down') {
          target_index++;
          if (target_index >= target_children.length) {
            target_index = 0;
          }
        } else {
          target_index--;
          if (target_index < 0) {
            target_index = target_children.length - 1;
          }
        }
        target_children[target_index]?.focus();
      }
    }
  },

  handleHorzArrow(ele, level, has_submenu, direction) {
    const current_level = ele.dataset.level;
    const isMobile = window.utilities.isMobile();

    // mobile and desktop menus are the same, but desktop top menu is 2
    if (direction === 'left' && current_level === '2' && !isMobile) {
      return;
    }

    if (level === 'menu') {
      const target_children = ele.closest('[role="menubar"]').querySelectorAll('[role="menuitem"]');
      const curr_index = Array.from(target_children).indexOf(ele);
      let target_index = curr_index;

      if (direction === 'right') {
        target_index++;
        if (target_index >= target_children.length) {
          target_index = 0;
        }
      } else {
        target_index--;
        if (target_index < 0) {
          target_index = target_children.length - 1;
        }
      }
      target_children[target_index]?.focus();
    } else {
      if (direction === 'left') {
        const target_parent = ele.closest('.submenu');
        const target_siblings = target_parent?.parentElement?.children;
        const curr_index = Array.from(target_siblings).indexOf(target_parent);

        const parent_button = target_siblings[curr_index - 1].querySelector('.navigation-drawer__button');

        parent_button.click();
        parent_button.focus();
      } else {
        if (has_submenu) {
          // Same as Action Keys
          this.handleActionKey(ele, level, has_submenu);
        } else {
          const target_parent = ele.closest('.submenu');
          const target_siblings = target_parent?.parentElement?.children;
          const curr_index = Array.from(target_siblings).indexOf(target_parent);

          const parent_button = target_siblings[curr_index - 1].querySelector('.navigation-drawer__button');
          parent_button.click();

          let start_index = curr_index + 1;
          if (start_index >= target_siblings.length - 1) {
            start_index = 0;
          }

          for (let i = start_index; i < target_siblings.length; i++) {
            const temp_button = target_siblings[i].querySelector('.navigation-drawer__button');

            if (temp_button) {
              //temp_button.click();
              temp_button.focus();
              break;
            }
          }
        }
      }
    }
  },

  handleJump(ele, level, has_submenu, direction) {
    let target_children;
    if (level === 'menu') {
      target_children = ele.closest('[role="menubar"]').querySelectorAll('[role="menuitem"]');
    } else {
      target_children = ele.closest('.submenu, .list-menu').querySelectorAll('.navigation-drawer__button');
    }

    if (direction === 'start') {
      target_children[0].focus();
    } else {
      target_children[target_children.length - 1].focus();
    }
  },

  isArrow(key) {
    return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
  },

  isMenuItem(ele) {
    return (
      ele.classList.contains('text-button__button') ||
      ele.classList.contains('navigation-drawer__button') ||
      ele.classList.contains('toggleable-icon-button')
    );
  },

  handleKeydown(event, { closeModal }) {
    let active_ele;
    let ele_level;
    let has_submenu;
    const isMobile = window.utilities.isMobile();

    isMobileCloseBtn = event.target?.parentElement?.classList.contains('close-btn');
    isHamburgerBtn = event.target.id === 'mobile-drawer-trigger';
    isDesktopCloseBtn = event.target.parentElement.classList.contains('slideout-menu-drawer__close');

    if (event.target.closest('.navigation-drawer__button')) {
      active_ele = event.target.closest('.navigation-drawer__button');
      ele_level = 'submenu';
    } else if (event.target.closest('[role="menuitem"]')) {
      active_ele = event.target.closest('[role="menuitem"]');
      ele_level = 'menu';
    } else {
      active_ele = document.querySelector('[role="menuitem"]');
      ele_level = 'menu';
    }

    has_submenu = active_ele.getAttribute('aria-haspopup');

    if (isHamburgerBtn || isMobileCloseBtn) {
      return;
    }

    // for desktop L2 menu is Level 1
    if (isDesktopCloseBtn && ele_level === 'menu' && !isMobile && (event.key === 'Enter' || event.key === ' ')) {
      closeModal();
      return;
    }

    // prevent other items inside the modal to work with arrow up or down
    // only menu elements should work with arrows
    // this avoids mobile menu to show
    if (this.isArrow(event.key) && !this.isMenuItem(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // allow for regular normal navigation if we are at level one, main menu
    // and desktop
    // Also prevent menu open and submenu open on Enter
    if ((event.key !== 'Tab' && ele_level !== 'menu' && !isMobile) || (event.key === 'Enter' && ele_level == 'menu')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    // prevents going back to L1 on desktop when there is no submenu
    if (has_submenu === null && event.key === 'ArrowRight') {
      return;
    }

    switch (event.key) {
      case 'Escape':
        if (typeof closeModal === 'function') closeModal();
        break;
      case 'Enter':
      case ' ':
        this.handleActionKey(active_ele, ele_level, has_submenu);
        break;
      case 'ArrowDown':
        this.handleVertArrow(active_ele, ele_level, has_submenu, 'down');
        break;
      case 'ArrowUp':
        this.handleVertArrow(active_ele, ele_level, has_submenu, 'up');
        break;
      case 'ArrowLeft':
        this.handleHorzArrow(active_ele, ele_level, has_submenu, 'left');
        break;
      case 'ArrowRight':
        this.handleHorzArrow(active_ele, ele_level, has_submenu, 'right');
        break;
      case 'Home':
        this.handleJump(active_ele, ele_level, has_submenu, 'start');
        break;
      case 'End':
        this.handleJump(active_ele, ele_level, has_submenu, 'end');
        break;
      default:
        return;
    }
  },
};

/**
 * Replaces only the text nodes (non-element content) inside a given element.
 *
 * @param {HTMLElement} element - The container element whose text content should be updated.
 * @param {string} newText - The new text to set (replaces only text nodes, not child elements).
 */
window.utilities.replaceTextOnly = (element, newText) => {
  if (!element) return;

  const sizeValueEl = element.querySelector('[data-size-value]');
  if (sizeValueEl) {
    sizeValueEl.textContent = String(newText).trim();
    return;
  }

  // Remove every text node so stale default sizes (e.g. Shopify's first variant) are not left behind.
  Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .forEach((node) => node.remove());

  const genderLabel = element.querySelector('.product-selector-label-current-selection--gender-label');
  const normalizedText = String(newText).trim();
  const displayText = normalizedText === '' ? '' : ` ${normalizedText}`;

  if (genderLabel) {
    genderLabel.insertAdjacentText('afterend', displayText);
    return;
  }

  element.appendChild(document.createTextNode(displayText));
};

/**
 * Safely converts a value to a number.
 *
 * - If the input is already a valid number, returns it as-is.
 * - If the input is a numeric string (e.g., "123", " 45.6 "), parses and returns the number.
 * - If the input cannot be safely converted to a number, returns `null`.
 *
 * @param {unknown} input - The value to convert.
 * @returns {number|null} The parsed number, or null if conversion is not possible.
 */
window.utilities.toNumber = (input) => {
  if (typeof input === 'number' && !isNaN(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }
  }

  return null;
};

window.utilities.openSearchModal = (searchText) => {
  const trigger = document.querySelector(`[aria-controls="search-slider"]`);
  const input = document.querySelector(`#search-suggestions-input`);

  if (trigger) {
    if (input) {
      const searchTextEvent = new CustomEvent('set-search-text', {
        detail: { text: searchText || '' },
      });
      input.dispatchEvent(searchTextEvent);
    }
    trigger.dispatchEvent(new Event('click'));
  }
};

/**
 * Parses a price string and extracts the numeric value.
 *
 * - Removes any currency symbols or non-numeric characters.
 * - Supports formats like "$10", "CA$10.00", "10,00 €", "USD 10.00", etc.
 * - Converts comma to dot for decimal handling.
 * - Returns a float or `null` if parsing fails.
 *
 * @param {string} priceString - The price string to parse.
 * @returns {number|null} The numeric value of the price, or null if invalid.
 */
window.utilities.parsePrice = (priceString) => {
  const match = priceString.replace(/[^\d.,-]/g, '').replace(',', '.');
  const value = parseFloat(match);
  return isNaN(value) ? null : value;
};

/**
 * Formats a price amount into a locale-specific currency string.
 *
 * - Removes trailing zeros from the formatted price.
 * - Rounds to 2 decimal places if the amount is not an integer.
 * - Uses the active currency from Shopify.
 *
 * @param {string} currency - The currency code to use for formatting.
 * @param {number|string} amount - The price amount to format.
 * @returns {string|number|null|undefined} The formatted price string, original value if unparseable, or empty string if formatting fails.
 */
window.utilities.formatPrice = (currency, amount) => {
  const localeCode = window.localeCode;

  // Handle null/undefined early
  if (amount === null || amount === undefined) return amount;

  let num;

  // If already a number, use it directly
  if (typeof amount === 'number' && !isNaN(amount)) {
    num = amount;
  } else {
    // Try to parse as price string
    num = window.utilities.parsePrice(String(amount));

    // If parsing failed, return original value
    if (num === null) {
      return amount;
    }
  }

  try {
    const formatted = Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currency || Shopify.currency.active,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currencyDisplay: 'narrowSymbol',
    }).format(num);

    // Remove trailing ".00" or locale-specific equivalent (like ",00")
    return formatted.replace(/([.,]00)(?=\D*$)/, '');
  } catch (error) {
    console.warn('Error formatting price:', error);
    return '';
  }
};

/**
 * Updates the cart line low-stock copy while edit-cart modals preview a variant.
 * Cart add / qty / done still re-render via Liquid in the cart-drawer section.
 */
window.utilities.updateCartLineLowStock = (holder, variant) => {
  if (!holder || !variant) return;

  const wrapperEl = holder.querySelector('[data-replaceable-cart-line-low-stock]');
  const textEl = holder.querySelector('[data-replaceable-cart-line-low-stock] .low-inventory-message--text');
  if (!textEl) return;

  const threshold = Number(window.product?.lowStockValue ?? 0);
  const qty = variant.inventory_quantity;
  const lowstockTemplate = window.product?.lowstock ?? '';

  if (!variant.available && qty <= 0) {
    const oosTemplate = window.product?.out_of_stock_variant ?? '';
    const sizeValue = variant.option2 || variant.option1 || variant.title?.split(' / ')?.pop() || '';

    textEl.textContent = oosTemplate.replace('{{ size_value }}', sizeValue);
    wrapperEl?.removeAttribute('hidden');
    return;
  }

  if (qty > 0 && qty <= threshold && lowstockTemplate) {
    textEl.textContent = lowstockTemplate.replace('{{ quantity }}', String(threshold));
    wrapperEl?.removeAttribute('hidden');
    return;
  }

  textEl.textContent = '';
  wrapperEl?.setAttribute('hidden', '');
};
